import { AnimTrack, KeyEasing } from './anim-track';
import { AnimTrackEditOp } from './edit-ops';
import { Events } from './events';

/**
 * Manages the active animation track and provides undo-wrapped
 * key operations. Resolves which track the user is interacting
 * with and ensures all mutations are undoable.
 *
 * When a scene camera is selected, its per-camera track is the active track.
 * Otherwise, the global camera animation track is used.
 */
const registerTrackManagerEvents = (events: Events) => {
    // Get the animation track of the currently active element.
    // If a scene camera is selected, return its per-camera track.
    const getActiveTrack = (): AnimTrack | null => {
        const selection = events.invoke('selection');
        if (selection && (selection as any).sceneCameraData) {
            return (selection as any).sceneCameraData.animTrack ?? null;
        }
        return events.invoke('camera.animTrack') ?? null;
    };

    // When selection changes, the active track may change — notify timeline to rebuild.
    events.on('selection.changed', () => {
        events.fire('track.keysLoaded');
    });

    // Helper: execute an edit on the active track wrapped in undo.
    // The editFn must return true if it modified the track, false if it was a no-op.
    const trackEdit = (name: string, editFn: (track: AnimTrack) => boolean) => {
        const track = getActiveTrack();
        if (!track) return;
        const before = track.snapshot();
        if (!editFn(track)) return;
        const after = track.snapshot();
        events.fire('edit.add', new AnimTrackEditOp(name, track, before, after), true);
    };

    // Get keys from active track
    events.function('track.keys', () => {
        const track = getActiveTrack();
        return track ? track.keys : [];
    });

    // Add key to active track
    events.on('track.addKey', (frame?: number) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit('addKey', track => track.addKey(keyFrame));
    });

    // Remove key from active track
    events.on('track.removeKey', (frame?: number) => {
        const keyFrame = frame ?? events.invoke('timeline.frame');
        trackEdit('removeKey', track => track.removeKey(keyFrame));
    });

    // Move key in active track
    events.on('track.moveKey', (fromFrame: number, toFrame: number) => {
        trackEdit('moveKey', track => track.moveKey(fromFrame, toFrame));
    });

    // Copy key in active track
    events.on('track.copyKey', (fromFrame: number, toFrame: number) => {
        trackEdit('copyKey', track => track.copyKey(fromFrame, toFrame));
    });

    // Get easing for a key
    events.function('track.getKeyEasing', (frame: number): KeyEasing | undefined => {
        const track = getActiveTrack();
        return track?.getKeyEasing(frame);
    });

    // Set easing for a key
    events.on('track.setKeyEasing', (frame: number, easing: KeyEasing | undefined) => {
        trackEdit('setKeyEasing', track => track.setKeyEasing(frame, easing));
    });
};

export { registerTrackManagerEvents };
